import { Badge } from "@/components/ui/badge";
import { Check, X, AlertTriangle, Shield, Phone, Mail, Globe } from "lucide-react";

const ICONS = { phone: Phone, email: Mail, ip: Globe };

function Row({ label, value, mono = true }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-800/60 last:border-0">
      <span className="text-xs font-mono uppercase tracking-wider text-slate-500 whitespace-nowrap">{label}</span>
      <span className={`text-sm text-right text-slate-200 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${
      ok ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
         : "bg-rose-500/10 text-rose-400 border border-rose-500/30"
    }`}>
      {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {label}
    </span>
  );
}

function RiskBadge({ level, score }) {
  const color = level === "high" ? "rose" : level === "medium" ? "amber" : "emerald";
  const map = {
    rose: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-mono border ${map[color]}`}>
      {level === "high" ? <AlertTriangle className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
      {level?.toUpperCase()} · {score}
    </span>
  );
}

export default function ResultCard({ kind, result, latency = 187 }) {
  const Icon = ICONS[kind] || Globe;
  if (!result) return null;

  return (
    <div data-testid="api-response-card" className="reveal rounded-xl border border-slate-800 bg-slate-900/60 backdrop-blur-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-950/60">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-cyan-signal" />
          <span className="font-mono text-xs uppercase tracking-widest text-slate-400">{kind} intel response</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> {latency}ms
          </span>
          <Badge className="bg-cyan-500/10 text-cyan-signal border border-cyan-500/20 hover:bg-cyan-500/10 font-mono text-[10px]">200 OK</Badge>
        </div>
      </div>

      <div className="p-5 space-y-1">
        {kind === "phone" && (
          <>
            <Row label="E.164" value={result.e164} />
            <Row label="Country" value={`${result.country_code} · ${result.country}`} />
            <Row label="Carrier" value={result.carrier} />
            <Row label="MCC / MNC" value={`${result.mcc} / ${result.mnc}`} />
            <Row label="Line Type" value={result.line_type?.toUpperCase()} />
            <Row label="Ported" value={<StatusPill ok={!result.ported} label={result.ported ? "yes" : "no"} />} mono={false} />
            <Row label="HLR Status" value={<StatusPill ok={result.hlr_status === "active"} label={result.hlr_status} />} mono={false} />
            <Row label="DNC Registry" value={<StatusPill ok={!result.dnc_registry} label={result.dnc_registry ? "listed" : "clear"} />} mono={false} />
            <Row label="Spam Risk" value={<RiskBadge level={result.spam_risk} score={result.spam_score} />} mono={false} />
          </>
        )}
        {kind === "email" && (
          <>
            <Row label="Email" value={result.email} />
            <Row label="Domain" value={result.domain} />
            <Row label="Syntax" value={<StatusPill ok={result.syntax_valid} label={result.syntax_valid ? "valid" : "invalid"} />} mono={false} />
            <Row label="MX Records" value={result.mx_records?.join(", ") || "—"} />
            <Row label="SMTP" value={<StatusPill ok={result.smtp_check} label={result.smtp_check ? "reachable" : "reject"} />} mono={false} />
            <Row label="Disposable" value={<StatusPill ok={!result.disposable} label={result.disposable ? "yes" : "no"} />} mono={false} />
            <Row label="Free Provider" value={result.free_provider ? "yes" : "no"} />
            <Row label="Role Account" value={result.role_account ? "yes" : "no"} />
            <Row label="Catch-all" value={result.catch_all ? "yes" : "no"} />
            <Row label="Deliverability" value={<RiskBadge level={result.status === "deliverable" ? "low" : result.status === "risky" ? "medium" : "high"} score={result.deliverability_score} />} mono={false} />
          </>
        )}
        {kind === "ip" && (
          <>
            <Row label="IP" value={result.ip} />
            <Row label="Version" value={result.version} />
            <Row label="Country" value={`${result.country_code} · ${result.country}`} />
            <Row label="City" value={result.city} />
            <Row label="Coordinates" value={`${result.latitude}, ${result.longitude}`} />
            <Row label="Timezone" value={result.timezone} />
            <Row label="ISP" value={result.isp} />
            <Row label="ASN" value={result.asn} />
            <Row label="Datacenter" value={<StatusPill ok={!result.is_datacenter} label={result.is_datacenter ? "yes" : "no"} />} mono={false} />
            <Row label="Proxy" value={<StatusPill ok={!result.is_proxy} label={result.is_proxy ? "yes" : "no"} />} mono={false} />
            <Row label="VPN" value={<StatusPill ok={!result.is_vpn} label={result.is_vpn ? "yes" : "no"} />} mono={false} />
            <Row label="Tor" value={<StatusPill ok={!result.is_tor} label={result.is_tor ? "yes" : "no"} />} mono={false} />
            <Row label="Risk" value={<RiskBadge level={result.risk_level} score={result.risk_score} />} mono={false} />
          </>
        )}
      </div>
    </div>
  );
}
